// Product analytics for switching map overlays on and off in the Maps sheet's
// overlays tab (Data overlays / Weather maps / My Trails).
//
// Split out from the Vue component so the event names, overlay ids and the
// before/after diff are one testable pure boundary shared with iOS/Android,
// which emit the same `overlay_added` / `overlay_removed` events with the same
// `overlay`, `category` and `screen` values through their own SDKs. Admin reporting
// counts `overlay_added` by `overlay` to see which overlays get used most.

import { HIKE_SOURCES, OVERLAY_TOGGLES, OverlayFlag, WEATHER_NAMES } from './onThisMap';

export const OVERLAY_ADDED_EVENT = 'overlay_added';
export const OVERLAY_REMOVED_EVENT = 'overlay_removed';

export type OverlayCategory = 'data_overlay' | 'weather' | 'my_trails';

/** Where the toggle came from. Only the overlays tab emits today. */
export type OverlayToggleSource = 'overlays_panel';

/**
 * The page the overlays tab was opened on. Values shared with iOS/Android use
 * their existing On This Map `screen` names; the rest are web-only pages.
 */
export type OverlayScreen =
  | 'main_map' | 'explore' | 'planner' | 'peak_list_map' | 'hike_overview_map'
  | 'recording_overview_map' | 'peak_3d' | 'park_map' | 'print_maps' | 'live_locator' | 'other';

// First match wins, so the more specific routes come first (e.g. a hike's
// /edit is the planner, not the hike overview). Mirrors config/routes.rb.
const SCREEN_ROUTES: Array<[RegExp, OverlayScreen]> = [
  [/^\/map$/, 'main_map'],
  [/^\/explore$/, 'explore'],
  [/^\/hike-planner$/, 'planner'],
  [/^\/hikes\/.+\/edit$/, 'planner'],
  [/^\/maps\/planned-trails(\/|$)/, 'planner'],
  [/^\/peaks\/map$/, 'peak_list_map'],
  [/^\/peak-list\/[^/]+\/[^/]+\/map$/, 'peak_list_map'],
  [/^\/peaks\/lists\/[^/]+\/map$/, 'peak_list_map'],
  [/^\/peaks\//, 'peak_3d'],
  [/^\/recordings\//, 'recording_overview_map'],
  [/^\/(hikes|trails)\//, 'hike_overview_map'],
  [/^\/parks\//, 'park_map'],
  [/^\/print-maps$/, 'print_maps'],
  [/^\/live-locator\//, 'live_locator'],
];

/** Which page a URL path is, for the `screen` property. Unknown paths are `other`. */
export function overlayScreenForPath(pathname: string): OverlayScreen {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  const match = SCREEN_ROUTES.find(([pattern]) => pattern.test(path));
  return match ? match[1] : 'other';
}

/**
 * Stable analytics id for each boolean overlay, keyed by its `onThisMap` key.
 * Named after what the user sees on the card, not the store flag, so the value
 * reads the same in PostHog as on every platform.
 */
export const DATA_OVERLAY_IDS: Record<typeof OVERLAY_TOGGLES[number]['key'], string> = {
  heatmap: 'heatmap',
  slope: 'slope_angle',
  cell: 'cell_coverage',
  officialHikes: 'hiiker_hikes',
  landAccess: 'public_access',
  cycling: 'cycling_routes',
};

/** Analytics id for each of the walker's own-hike line sources. */
export const MY_TRAILS_OVERLAY_IDS: Record<typeof HIKE_SOURCES[number], string> = {
  get_user_recordings: 'recordings',
  get_user_planned_trails: 'my_hikes',
  get_user_completed_hikes: 'completed',
};

/** The slice of `mapDataOverlays` state the diff reads. */
export interface OverlaySelection {
  flags: Record<OverlayFlag, boolean>;
  weather: string | null;
  activeSources: string[];
}

export interface OverlayToggleEventProps {
  overlay: string;
  category: OverlayCategory;
  source: OverlayToggleSource;
  screen: OverlayScreen;
  /** How many overlays (all categories) are on after this change. */
  active_overlay_count: number;
}

export interface OverlayToggleEvent {
  event: typeof OVERLAY_ADDED_EVENT | typeof OVERLAY_REMOVED_EVENT;
  props: OverlayToggleEventProps;
}

/**
 * Reads the selection out of the `mapDataOverlays` module state. Trail hazards
 * (`hazardsActive`) are not an Overlays-tab overlay: they have their own switch
 * and their own `hazards_toggled` event, so they never count towards
 * `active_overlay_count`.
 */
export function overlaySelection(state: any): OverlaySelection {
  const flags = {} as Record<OverlayFlag, boolean>;
  OVERLAY_TOGGLES.forEach(({ flag }) => { flags[flag] = !!state?.[flag]; });
  return {
    flags,
    weather: state?.activeWeatherLayer?.name ?? null,
    activeSources: [...(state?.activeSources ?? [])],
  };
}

/** Every overlay that is on, as `[overlayId, category]` pairs. */
function activeOverlays(selection: OverlaySelection): Array<[string, OverlayCategory]> {
  const on: Array<[string, OverlayCategory]> = [];
  OVERLAY_TOGGLES.forEach(({ key, flag }) => {
    if (selection.flags[flag]) on.push([DATA_OVERLAY_IDS[key], 'data_overlay']);
  });
  if (selection.weather && (WEATHER_NAMES as readonly string[]).includes(selection.weather)) {
    on.push([selection.weather, 'weather']);
  }
  HIKE_SOURCES.forEach((src) => {
    if (selection.activeSources.includes(src)) on.push([MY_TRAILS_OVERLAY_IDS[src], 'my_trails']);
  });
  return on;
}

/**
 * The events one toggle produced, from the selection before and after it.
 * Usually one event; picking a second weather map yields two, because weather
 * maps are one-at-a-time and the new one replaces the old (removed first).
 */
export function overlayToggleEvents(
  before: OverlaySelection,
  after: OverlaySelection,
  screen: OverlayScreen,
  source: OverlayToggleSource = 'overlays_panel',
): OverlayToggleEvent[] {
  const was = activeOverlays(before);
  const now = activeOverlays(after);
  const wasIds = was.map(([id]) => id);
  const nowIds = now.map(([id]) => id);
  const active_overlay_count = now.length;

  const removed = was
    .filter(([id]) => !nowIds.includes(id))
    .map(([overlay, category]): OverlayToggleEvent => ({
      event: OVERLAY_REMOVED_EVENT,
      props: { overlay, category, source, screen, active_overlay_count },
    }));
  const added = now
    .filter(([id]) => !wasIds.includes(id))
    .map(([overlay, category]): OverlayToggleEvent => ({
      event: OVERLAY_ADDED_EVENT,
      props: { overlay, category, source, screen, active_overlay_count },
    }));
  return [...removed, ...added];
}

/** Fire-and-forget; never let a missing analytics shim break a toggle. */
export function trackOverlayToggle(
  before: OverlaySelection,
  after: OverlaySelection,
  screen: OverlayScreen = overlayScreenForPath(window.location.pathname),
  source: OverlayToggleSource = 'overlays_panel',
): void {
  try {
    overlayToggleEvents(before, after, screen, source).forEach(({ event, props }) => {
      window.mixpanel?.track(event, props);
    });
  } catch {}
}
